import { 
  Lock, 
  AlertCircle, 
  Info, 
  Wrench, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  X 
} from 'lucide-react';
import ReactDOM from 'react-dom';
import { sanitizeHtmlAllowSafe } from '../utils/sanitize-html';

interface AccessGatePreviewProps {
  title: string;
  description: string;
  buttonText: string;
  icon?: string;
  theme?: 'default' | 'warning' | 'danger' | 'info' | 'success';
  allowClose?: boolean;
  showButton?: boolean;
  showInfo?: boolean; // ✅ NEW - Toggle for info box visibility
  subtitle?: string; // ✅ NEW
  infoText?: string; // ✅ NEW
  footerText?: string; // ✅ NEW
  contactEmail?: string; // ✅ NEW
  isFullscreen?: boolean;
  onClose?: () => void;
  forceShowCloseButton?: boolean; // ✅ NEW - Force show X button in admin preview
  /** When set (e.g. 10001), portal is shown above admin overlay (z-[10000]) */
  portalZIndex?: number;
}

// Icon mapping
const iconMap: Record<string, any> = {
  Lock,
  AlertCircle,
  Wrench,
  Shield,
  AlertTriangle,
  CheckCircle,
  Info
};

// Theme configurations
const themeConfig = {
  default: {
    gradient: 'from-purple-50 via-pink-50 to-orange-50',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    buttonGradient: 'from-purple-600 to-pink-600',
    borderColor: 'border-purple-100',
    infoBg: 'bg-purple-50',
    infoBorder: 'border-purple-100',
    infoText: 'text-purple-900',
    infoIcon: 'text-purple-600'
  },
  warning: {
    gradient: 'from-orange-50 via-yellow-50 to-amber-50',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    buttonGradient: 'from-orange-600 to-amber-600',
    borderColor: 'border-orange-100',
    infoBg: 'bg-orange-50',
    infoBorder: 'border-orange-100',
    infoText: 'text-orange-900',
    infoIcon: 'text-orange-600'
  },
  danger: {
    gradient: 'from-red-50 via-rose-50 to-pink-50',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    buttonGradient: 'from-red-600 to-rose-600',
    borderColor: 'border-red-100',
    infoBg: 'bg-red-50',
    infoBorder: 'border-red-100',
    infoText: 'text-red-900',
    infoIcon: 'text-red-600'
  },
  info: {
    gradient: 'from-blue-50 via-cyan-50 to-sky-50',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    buttonGradient: 'from-blue-600 to-cyan-600',
    borderColor: 'border-blue-100',
    infoBg: 'bg-blue-50',
    infoBorder: 'border-blue-100',
    infoText: 'text-blue-900',
    infoIcon: 'text-blue-600'
  },
  success: {
    gradient: 'from-green-50 via-emerald-50 to-teal-50',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    buttonGradient: 'from-green-600 to-emerald-600',
    borderColor: 'border-green-100',
    infoBg: 'bg-green-50',
    infoBorder: 'border-green-100',
    infoText: 'text-green-900',
    infoIcon: 'text-green-600'
  }
};

export function AccessGatePreview({ 
  title, 
  description, 
  buttonText,
  icon = 'Lock',
  theme = 'default',
  allowClose = true,
  showButton = true,
  showInfo = true, // ✅ NEW - Default to show info box
  subtitle = 'Beta-tilgang påkrevd',
  infoText = '<strong>Pilot-fase:</strong> Talentfunn er for øyeblikket i en lukket beta-fase.',
  footerText = 'Forhåndsvisning av access gate modal',
  contactEmail = 'kontakt@talentfunn.no?subject=Pilot-program',
  isFullscreen = false,
  onClose,
  forceShowCloseButton = false, // ✅ NEW - Default to false
  portalZIndex
}: AccessGatePreviewProps) {
  const themeStyles = themeConfig[theme];
  const IconComponent = iconMap[icon] || Lock;

  const content = (
    <div className={`${isFullscreen ? 'min-h-screen' : 'h-full'} bg-gradient-to-br ${themeStyles.gradient} flex items-center justify-center p-4`}>
      <div className={`${isFullscreen ? 'max-w-md' : 'max-w-xs'} w-full relative`}>
        {/* Logo - Show in both fullscreen AND inline preview */}
        <div className="text-center mb-8">
          <div className={`${isFullscreen ? 'h-16' : 'h-12'} flex items-center justify-center mb-4`}>
            <svg 
              className={isFullscreen ? 'h-12' : 'h-10'} 
              viewBox="0 0 284.81 55.39" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#9333ea"/>
                  <stop offset="100%" stopColor="#ec4899"/>
                </linearGradient>
              </defs>
              <g>
                <path fill="url(#gradient1)" d="M36.66,21.12l-10.1-1.91c-.87-.16-1.54-.83-1.7-1.7l-1.91-10.1c-.25-1.34-1.32-2.4-2.65-2.65-.87-.16-1.75.02-2.48.52-.73.5-1.22,1.25-1.39,2.12l-1.91,10.1c-.16.87-.83,1.54-1.7,1.7l-10.1,1.91c-1.34.25-2.4,1.32-2.65,2.65-.16.87.02,1.75.52,2.48s1.26,1.22,2.12,1.39l10.1,1.91c.87.16,1.54.83,1.7,1.7l1.91,10.1c.25,1.34,1.32,2.4,2.65,2.65.2.04.41.06.61.06,1.57,0,2.96-1.12,3.26-2.71l1.91-10.1c.16-.87.83-1.54,1.7-1.7l10.1-1.91c1.34-.25,2.4-1.31,2.65-2.65.34-1.8-.85-3.53-2.65-3.87ZM36.1,24.69l-10.1,1.91c-2.09.4-3.7,2-4.09,4.09l-1.91,10.11c-.03.17-.2.29-.37.25-.13-.02-.23-.13-.25-.26l-1.91-10.1c-.39-2.09-2-3.7-4.09-4.09l-10.11-1.91c-.11-.02-.17-.08-.2-.13-.03-.05-.07-.13-.05-.24.02-.13.13-.23.26-.25l10.1-1.91c2.09-.4,3.7-2,4.09-4.09l1.91-10.1s0,0,0,0c.02-.11.08-.17.13-.2.04-.03.1-.06.18-.06.02,0,.04,0,.06,0,.13.02.23.13.25.26l1.91,10.1c.4,2.09,2,3.7,4.09,4.09l10.11,1.91c.17.03.29.2.25.37-.02.13-.13.23-.26.26Z"/>
                <path fill="url(#gradient1)" d="M30.59,11.33h2.14v2.14c0,.83.67,1.5,1.5,1.5s1.5-.67,1.5-1.5v-2.14h2.14c.83,0,1.5-.67,1.5-1.5s-.67-1.5-1.5-1.5h-2.14v-2.14c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5,1.5v2.14h-2.14c-.83,0-1.5.67-1.5,1.5s.67,1.5,1.5,1.5Z"/>
                <circle fill="url(#gradient1)" cx="5.14" cy="38.92" r="5.14"/>
                <text fill="url(#gradient1)" fontFamily="Roboto, Arial, sans-serif" fontSize="49" x="52" y="42">Talentfunn</text>
              </g>
            </svg>
          </div>
          <p className="text-gray-600 text-sm">
            {subtitle}
          </p>
        </div>

        {/* Access form */}
        <div className={`bg-white rounded-2xl shadow-xl ${isFullscreen ? 'p-8' : 'p-4'} border ${themeStyles.borderColor}`}>
          <div className={`flex items-center gap-3 ${isFullscreen ? 'mb-6 pb-6' : 'mb-4 pb-4'} border-b border-gray-100`}>
            <div className={`${isFullscreen ? 'w-10 h-10' : 'w-8 h-8'} ${themeStyles.iconBg} rounded-full flex items-center justify-center`}>
              <IconComponent className={`${isFullscreen ? 'w-5 h-5' : 'w-4 h-4'} ${themeStyles.iconColor}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className={`${isFullscreen ? 'text-xl' : 'text-base'} font-semibold text-gray-900 truncate`}>
                {title}
              </h2>
              <p className={`${isFullscreen ? 'text-sm' : 'text-xs'} text-gray-600 truncate`}>
                {description}
              </p>
            </div>
          </div>

          <div className={`space-y-${isFullscreen ? '5' : '3'}`}>
            <div>
              <label className={`block ${isFullscreen ? 'text-sm' : 'text-xs'} font-medium text-gray-700 ${isFullscreen ? 'mb-2' : 'mb-1'}`}>
                Brukernavn
              </label>
              <div className={`w-full ${isFullscreen ? 'px-4 py-3' : 'px-3 py-2'} border border-gray-300 rounded-lg bg-gray-50`}>
                <span className={`${isFullscreen ? 'text-sm' : 'text-xs'} text-gray-400`}>Brukernavn</span>
              </div>
            </div>

            <div>
              <label className={`block ${isFullscreen ? 'text-sm' : 'text-xs'} font-medium text-gray-700 ${isFullscreen ? 'mb-2' : 'mb-1'}`}>
                Passord
              </label>
              <div className={`w-full ${isFullscreen ? 'px-4 py-3' : 'px-3 py-2'} border border-gray-300 rounded-lg bg-gray-50`}>
                <span className={`${isFullscreen ? 'text-sm' : 'text-xs'} text-gray-400`}>••••••••</span>
              </div>
            </div>

            {showButton && (
              <button
                disabled
                className={`w-full bg-gradient-to-r ${themeStyles.buttonGradient} text-white ${isFullscreen ? 'py-3 px-6' : 'py-2 px-4'} rounded-lg font-semibold opacity-90 cursor-not-allowed ${isFullscreen ? 'text-base' : 'text-sm'}`}
              >
                {buttonText}
              </button>
            )}
          </div>

          {/* Info box - Show in both inline and fullscreen */}
          {showInfo && (
            <div className={`${isFullscreen ? 'mt-6' : 'mt-4'} p-${isFullscreen ? '4' : '3'} ${themeStyles.infoBg} rounded-lg border ${themeStyles.infoBorder}`}>
              <div className="flex items-start gap-2">
                <Info className={`${isFullscreen ? 'w-5 h-5' : 'w-4 h-4'} ${themeStyles.infoIcon} flex-shrink-0 mt-0.5`} />
                <p className={`text-xs ${themeStyles.infoText} leading-relaxed`} dangerouslySetInnerHTML={{ __html: sanitizeHtmlAllowSafe(infoText) }} />
              </div>
            </div>
          )}
        </div>

        {/* Footer - Show in both inline and fullscreen */}
        <div className={`text-center ${isFullscreen ? 'mt-8' : 'mt-4'}`}>
          <p className={`${isFullscreen ? 'text-sm' : 'text-xs'} text-gray-600`}>
            {footerText}
          </p>
        </div>
      </div>
    </div>
  );

  // Fullscreen modal
  if (isFullscreen && onClose) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: portalZIndex ?? 'var(--z-modal)' }}>
        <div className="relative w-full max-w-2xl max-h-[90vh] overflow-auto rounded-2xl shadow-2xl">
          {/* Close button - positioned on fullscreen container */}
          {(allowClose || forceShowCloseButton) && (
            <button
              onClick={onClose}
              className="absolute -top-3 -right-3 w-10 h-10 bg-white rounded-full shadow-2xl flex items-center justify-center hover:bg-gray-100 transition-colors z-50 border-2 border-gray-200"
              aria-label="Lukk forhåndsvisning"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
          )}
          {content}
        </div>
      </div>,
      document.body
    );
  }

  // Inline preview
  return (
    <div className="relative w-full h-[500px] rounded-xl overflow-auto border-2 border-gray-200 shadow-lg">
      {content}
    </div>
  );
}
